import { createContext, useContext, useEffect, useState } from 'react'
import { collection, getDocs, doc, setDoc, getDoc } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { APP_CONFIG_DEFAULTS, TIER_SEED_DATA } from '../lib/tierSeedData'

export { APP_CONFIG_DEFAULTS, TIER_SEED_DATA }

function normalizeBoolean(val) {
  return val === true || String(val).toLowerCase() === 'true'
}

function flattenAppConfiguration(raw) {
  const app = raw.app || {}
  const contactForm = raw.contactForm || {}
  const features = raw.features || {}
  const fileUpload = raw.fileUpload || {}
  const newUserDefaults = raw.newUserDefaults || {}
  const promoHeader = app.PromoHeader ?? raw.PromoHeader
  const welcomeEmail10Off = app.WelcomeEmail10Off ?? raw.WelcomeEmail10Off
  const youtubeDemoVideo = app.youtubeDemoVideo ?? raw.youtubeDemoVideo ?? ''
  const carouselVideos = app.carouselVideos ?? raw.carouselVideos ?? ''
  const carouselVideoHeaders = app.carouselVideoHeaders ?? raw.carouselVideoHeaders ?? ''
  const showCarouselVideos = app.showCarouselVideos ?? raw.showCarouselVideos
  return {
    draftExpiryMs: app.draftExpiryMs,
    oneTimePrice: app.oneTimePrice,
    maintenance: app.maintenance === true,
    showAdminButton: app.showAdminButton === true || String(app.showAdminButton).toLowerCase() === 'true',
    PromoHeader: normalizeBoolean(promoHeader),
    WelcomeEmail10Off: normalizeBoolean(welcomeEmail10Off),
    contactMessageLimit: contactForm.messageLimit,
    contactAttachmentLimit: contactForm.attachmentLimit,
    contactAttachmentMaxBytes: contactForm.attachmentMaxBytes,
    hideLogoFeature: features.hideLogoFeature,
    hideVerificationFeature: features.hideVerificationFeature,
    maxCustomFields: fileUpload.maxCustomFields,
    maxPdfBytes: fileUpload.maxPdfBytes,
    logoMaxBytes: fileUpload.logoMaxBytes,
    logoMaxDimension: fileUpload.logoMaxDimension,
    maxAttachmentBytes: fileUpload.maxAttachmentBytes,
    newUserDefaults,
    freeDraft: raw.freeDraft,
    freeDraftAfterFreeSend: raw.freeDraftAfterFreeSend,
    youtubeDemoVideo: typeof youtubeDemoVideo === 'string' ? youtubeDemoVideo.trim() : '',
    carouselVideos: typeof carouselVideos === 'string' ? carouselVideos.trim() : '',
    carouselVideoHeaders: typeof carouselVideoHeaders === 'string' ? carouselVideoHeaders.trim() : '',
    showCarouselVideos: normalizeBoolean(showCarouselVideos),
  }
}

const DEFAULT_TIERS = TIER_SEED_DATA
const SubscriptionTiersContext = createContext({ tiers: DEFAULT_TIERS, appConfig: APP_CONFIG_DEFAULTS })

export async function seedSubscriptionTiersToFirestore() {
  for (const [id, data] of Object.entries(TIER_SEED_DATA)) {
    await setDoc(doc(db, 'subscriptionTiers', id), data, { merge: true })
  }
}

export function SubscriptionTiersProvider({ children }) {
  const [tiers, setTiers] = useState(DEFAULT_TIERS)
  const [appConfig, setAppConfig] = useState(APP_CONFIG_DEFAULTS)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    let idleId = null
    let timeoutId = null
    const fetchAll = async () => {
      try {
        const [tiersSnap, appConfigSnap, legacyAppConfigSnap] = await Promise.all([
          getDocs(collection(db, 'subscriptionTiers')),
          getDoc(doc(db, 'appConfiguration', 'app')),
          getDoc(doc(db, 'appConfig', 'app')),
        ])
        if (cancelled) return
        const map = { ...TIER_SEED_DATA }
        tiersSnap.docs.forEach((d) => {
          const id = d.id
          if (TIER_SEED_DATA[id]) {
            map[id] = { ...TIER_SEED_DATA[id], ...d.data() }
          }
        })
        setTiers(map)
        if (appConfigSnap.exists()) {
          const raw = appConfigSnap.data()
          const flat = flattenAppConfiguration(raw)
          setAppConfig({
            ...APP_CONFIG_DEFAULTS,
            ...flat,
            freeDraft: { ...APP_CONFIG_DEFAULTS.freeDraft, ...(flat.freeDraft || {}) },
            freeDraftAfterFreeSend: { ...APP_CONFIG_DEFAULTS.freeDraftAfterFreeSend, ...(flat.freeDraftAfterFreeSend || {}) },
          })
        } else if (legacyAppConfigSnap.exists()) {
          const legacy = legacyAppConfigSnap.data()
          setAppConfig({ ...APP_CONFIG_DEFAULTS, ...legacy, contactMessageLimit: 1000, maxCustomFields: 20 })
        }
      } catch (err) {
        if (!cancelled) console.warn('[SubscriptionTiers] Using defaults:', err?.message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    const run = () => {
      if (!cancelled) fetchAll()
    }
    if (typeof requestIdleCallback !== 'undefined') {
      idleId = requestIdleCallback(run, { timeout: 2200 })
    } else {
      timeoutId = setTimeout(run, 1)
    }
    return () => {
      cancelled = true
      if (idleId != null && typeof cancelIdleCallback === 'function') cancelIdleCallback(idleId)
      if (timeoutId != null) clearTimeout(timeoutId)
    }
  }, [])

  return (
    <SubscriptionTiersContext.Provider value={{ tiers, appConfig, loading }}>
      {children}
    </SubscriptionTiersContext.Provider>
  )
}

export function useSubscriptionTiers() {
  const ctx = useContext(SubscriptionTiersContext)
  return ctx?.tiers ?? DEFAULT_TIERS
}

export function useAppConfig() {
  const ctx = useContext(SubscriptionTiersContext)
  return ctx?.appConfig ?? APP_CONFIG_DEFAULTS
}

export function useSubscriptionTierConfig(tierId, isOneTimeUse = false) {
  const tiers = useSubscriptionTiers()
  const premium = tiers.premium ?? TIER_SEED_DATA.premium ?? {}
  const tierData = (tiers[tierId] ?? TIER_SEED_DATA[tierId]) ?? premium
  const base = isOneTimeUse ? { ...premium, maxEmailsPerMonth: 0 } : tierData
  return {
    ...base,
    maxEmailsPerMonth: isOneTimeUse ? 0 : (tierData.maxEmailsPerMonth ?? 150),
    maxSignedAgreementsPerMonth: base.maxSignedAgreementsPerMonth ?? 150,
    maxEmailsSignedPerMonth: base.maxEmailsSignedPerMonth ?? 150,
  }
}
